import React, {
	type RefObject,
	type MutableRefObject,
	type ReactNode,
	useCallback,
	useMemo,
	useEffect,
	useState,
	useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { css, styled } from '@compiled/react';
import DropdownMenu, {
	type OnOpenChangeArgs,
	DropdownItem,
	DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import DragHandlerIcon from '@atlaskit/icon/glyph/drag-handler';
import {
	attachClosestEdge,
	type Edge,
	extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';

import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box-without-terminal';
import {
	draggable,
	dropTargetForElements,
	monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { ShortcutScope } from '@atlassian/jira-common-components-keyboard-shortcuts';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	SORT_NONE,
	getMarginBetweenHeaders,
	getDragIconSize,
} from '../../../../../common/constants';
import type { Column, ColumnId, ReorderColumn } from '../../../../../common/types';
import { ConditionalWrapper } from '../../../../../common/ui/conditional-wrapper';
import { getSortOrderConfig } from '../../../../../common/ui/header-cell/constants';
import { ColumnName, SortIcon } from '../../../../../common/ui/header-cell/styled';
import WarningIndicator from '../../../../../common/ui/warning-indicator';
import { HEADER_CELL_CONTENT_SELECTOR, SORT_ICON_SELECTOR } from './constants';
import messages from './messages';

type HeaderState =
	| {
			type: 'idle';
	  }
	| {
			type: 'preview';
			container: HTMLElement;
			previewWidth: number;
	  }
	| {
			type: 'dragging';
	  }
	| {
			type: 'drop-target';
			closestEdge: Edge | null;
	  };

const idleState: HeaderState = { type: 'idle' };
const draggingState: HeaderState = { type: 'dragging' };

type ColumnType = 'first' | 'middle' | 'last';

function getColumnType(index: number, totalColumns: number): ColumnType {
	if (index === 0) {
		return 'first';
	}
	if (index === totalColumns - 1) {
		return 'last';
	}
	return 'middle';
}

const DRAG_PREVIEW_MAX_WIDTH = 260;

type Props = {
	column: Column;
	headerRef: MutableRefObject<HTMLTableCellElement | null>;
	index: number;
	instanceId: symbol;
	isDraggable: boolean;
	isSortable: boolean;
	reorderColumn: ReorderColumn;
	totalColumns: number;
	totalColumnsExcludingMenu: number;
	visibleTableHeight: number;
};

export const InteractiveTableHeaderCellContent = ({
	column,
	headerRef,
	index,
	instanceId,
	isDraggable,
	isSortable,
	reorderColumn,
	totalColumns,
	totalColumnsExcludingMenu,
	visibleTableHeight,
}: Props) => {
	if (!isDraggable && !isSortable) {
		throw new Error(
			'InteractiveTableHeaderCellContent should only be rendered for interactive header cells',
		);
	}

	const intl = useIntl();
	const [state, setState] = useState<HeaderState>(idleState);
	const [isDropdownMenuOpen, setIsDropdownMenuOpen] = useState(false);
	const draggableRef = useRef<HTMLDivElement | null>(null);
	const dropTargetRef = useRef<HTMLDivElement | null>(null);

	const {
		ascText,
		currentSorting: sortOrder = SORT_NONE,
		descText,
		id,
		jqlTerm,
		type,
		onSortOrderChanged,
		title,
		warningText,
	} = column;

	const isFirstColumn = index === 0;
	const isLastColumn = index === totalColumnsExcludingMenu - 1;

	// Detect whether we should render a drop target
	useEffect(
		() =>
			monitorForElements({
				canMonitor({ source }) {
					return source.data.instanceId === instanceId && source.data.id !== id;
				},
				onDragStart() {
					setState({
						type: 'drop-target',
						closestEdge: null,
					});
				},
				onDrop() {
					setState(idleState);
				},
			}),
		[id, instanceId],
	);

	// Dynamically create a drop target after drag starts. We're making the drop target the full height
	// of the table so that a column can be reordered by dragging anywhere on the edge of the column.
	useEffect(() => {
		if (state.type !== 'drop-target') {
			return;
		}

		const dropTarget = dropTargetRef.current;
		if (!dropTarget) {
			return;
		}

		return dropTargetForElements({
			element: dropTarget,
			getData({ input, element }) {
				const data = { id, index };
				return attachClosestEdge(data, {
					input,
					element,
					allowedEdges: ['left', 'right'],
				});
			},
			canDrop({ source }) {
				return source.data.instanceId === instanceId && source.data.id !== id;
			},
			onDrag({ self }) {
				setState((current) => {
					if (current.type !== 'drop-target') {
						return current;
					}

					const closestEdge = extractClosestEdge(self.data);
					if (current.closestEdge === closestEdge) {
						return current;
					}

					return {
						type: 'drop-target',
						closestEdge,
					};
				});
			},
			onDragLeave() {
				setState({
					type: 'drop-target',
					closestEdge: null,
				});
			},
			onDrop() {
				setState(idleState);
			},
		});
	}, [id, index, instanceId, state.type]);

	// Set up the draggable element
	useEffect(() => {
		if (!isDraggable) {
			return;
		}

		const header = headerRef.current;
		const element = draggableRef.current;
		if (!header || !element) {
			return;
		}

		return draggable({
			element,
			getInitialData() {
				return { id, index, instanceId };
			},
			onGenerateDragPreview({ nativeSetDragImage }) {
				const headerWidth = header.getBoundingClientRect().width;
				const previewWidth = Math.min(headerWidth, DRAG_PREVIEW_MAX_WIDTH);
				setCustomNativeDragPreview({
					// Position the drag preview over the top of the column
					getOffset: () => ({ x: 0, y: 20 }),
					render: ({ container }) => {
						// Cause a `react` re-render to create the portal synchronously
						setState({
							type: 'preview',
							container,
							previewWidth,
						});
						// Cause a `react` re-render to remove the portal
						return () => setState(draggingState);
					},
					nativeSetDragImage,
				});
			},
			onDragStart() {
				setState(draggingState);
			},
			onDrop() {
				setState(idleState);
			},
		});
	}, [headerRef, id, index, instanceId, isDraggable]);

	// Close the dropdown menu when a drag starts on any column
	useEffect(() => {
		if (state.type !== 'idle') {
			setIsDropdownMenuOpen(false);
		}
	}, [state.type]);

	const { getIcon, getNextState, getTooltipSuffix, isIconPersistent } = getSortOrderConfig({
		sortOrder,
		intl,
	});

	const tooltipContent = useMemo(
		() => `${title} • ${getTooltipSuffix({ ascText, descText })}`,
		[ascText, descText, getTooltipSuffix, title],
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleSortClick = useCallback(() => {
		const nextState = getNextState();

		const event = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
		});

		fireUIAnalytics(event, 'button clicked', 'sortOrderChange', {
			fieldType: type,
			sortOrder: nextState,
		});

		if (onSortOrderChanged && jqlTerm != null) {
			// TODO EM-5150 Ideally API should be providing aliases alongside jqlTerm
			// TODO In future we should remove this type restriction and allow any string value as column id.
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			onSortOrderChanged(jqlTerm as ColumnId, nextState);
		}
	}, [getNextState, createAnalyticsEvent, type, onSortOrderChanged, jqlTerm]);

	const withSortButton = useCallback(
		(children: ReactNode) => (
			<Tooltip content={tooltipContent}>
				<SortButton
					isDraggable={isDraggable}
					isDragging={state.type === 'dragging'}
					onClick={handleSortClick}
				>
					{children}
				</SortButton>
			</Tooltip>
		),
		[handleSortClick, isDraggable, state.type, tooltipContent],
	);

	const moveLeft = useCallback(() => {
		reorderColumn({
			startIndex: index,
			indexOfTarget: index - 1,
		});
	}, [reorderColumn, index]);

	const moveRight = useCallback(() => {
		reorderColumn({
			startIndex: index,
			indexOfTarget: index + 1,
		});
	}, [reorderColumn, index]);

	const onDropdownMenuOpenChange = useCallback(({ isOpen }: OnOpenChangeArgs) => {
		setIsDropdownMenuOpen(isOpen);
	}, []);

	return (
		<>
			<CellContentContainer
				data-component-selector={HEADER_CELL_CONTENT_SELECTOR}
				isDropdownMenuOpen={isDropdownMenuOpen}
				isDraggable={isDraggable}
				isSortable={isSortable}
				isIconPersistent={isIconPersistent}
				ref={draggableRef}
			>
				{isDraggable && (
					<DropdownMenu
						isOpen={isDropdownMenuOpen}
						onOpenChange={onDropdownMenuOpenChange}
						testId={`native-issue-table.ui.issue-table.table-header-old.table-header-cell.interactive.${id}-reorder-menu`}
						trigger={({ testId, triggerRef, ...triggerProps }) => (
							<DragHandleButton
								{...triggerProps}
								data-testid={testId}
								// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
								ref={triggerRef as RefObject<HTMLButtonElement>}
							>
								<DragHandleIconContainer>
									<DragHandlerIcon
										label={intl.formatMessage(messages.reorderColumn, {
											title,
										})}
										size="medium"
										primaryColor={token('color.icon.subtle', '#626F86')}
									/>
								</DragHandleIconContainer>
							</DragHandleButton>
						)}
					>
						<DropdownItemGroup>
							<DropdownItem isDisabled={isFirstColumn} onClick={moveLeft}>
								{intl.formatMessage(messages.moveLeft)}
							</DropdownItem>
							<DropdownItem isDisabled={isLastColumn} onClick={moveRight}>
								{intl.formatMessage(messages.moveRight)}
							</DropdownItem>
							<ShortcutScope />
						</DropdownItemGroup>
					</DropdownMenu>
				)}
				<ConditionalWrapper condition={isSortable} renderWrapper={withSortButton}>
					<CellContent>
						{warningText && <WarningIndicator tooltip={warningText} />}
						<ColumnName>{title}</ColumnName>
						{isSortable && (
							<SortIcon
								data-component-selector={SORT_ICON_SELECTOR}
								isIconPersistent={isIconPersistent}
							>
								{getIcon()}
							</SortIcon>
						)}
					</CellContent>
				</ConditionalWrapper>
			</CellContentContainer>
			{state.type === 'drop-target' && (
				<DropTarget
					ref={dropTargetRef}
					height={visibleTableHeight}
					columnType={getColumnType(index, totalColumns)}
					data-testid={`native-issue-table.ui.issue-table.table-header-old.table-header-cell.interactive.${id}-drop-target`}
				>
					{state.closestEdge && <DropIndicator edge={state.closestEdge} />}
				</DropTarget>
			)}
			{state.type === 'preview' &&
				createPortal(
					<DragPreview
						height={visibleTableHeight}
						width={state.previewWidth}
						data-testid={`native-issue-table.ui.issue-table.table-header-old.table-header-cell.interactive.${id}-drag-preview`}
					/>,
					state.container,
				)}
		</>
	);
};

export default InteractiveTableHeaderCellContent;

// Pragmatic DnD experience styles
const buttonHeight = '28px';
const dragHandleOpacityCssVar = '--drag-handle-opacity';
const dragHandleWidthCssVar = '--drag-handle-width';

const dragHandleStyles = css({
	[dragHandleOpacityCssVar]: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	[dragHandleWidthCssVar]: getDragIconSize(),
	backgroundColor: token('color.background.neutral.subtle.hovered', '#091E420F'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CellContentContainer = styled.div<{
	isDropdownMenuOpen: boolean;
	isDraggable: boolean;
	isIconPersistent: boolean;
	isSortable: boolean;
}>(
	{
		[dragHandleOpacityCssVar]: 0,
		[dragHandleWidthCssVar]: 8,
		display: 'inline-flex',
		alignItems: 'center',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		marginRight: `calc(${getDragIconSize()} - ${token('space.100', '8px')} + ${getMarginBetweenHeaders()})`,
		fontWeight: '500',
		borderRadius: token('border.radius.100', '4px'),
		transition: 'margin-right 150ms ease-in-out',
		// Toggling the opacity/width of the drag handle here as we want it to be visible
		// when hovering over the drag handle or the sort button.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		':hover, :focus-within': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			...dragHandleStyles,
			// when the drag handle is visible, margin needs to be adjusted to accomodate
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
			marginRight: ({ isDraggable, isDropdownMenuOpen }) =>
				isDraggable || isDropdownMenuOpen
					? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						getMarginBetweenHeaders()
					: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						`calc(${getDragIconSize()} - ${token('space.100', '8px')} + ${getMarginBetweenHeaders()})`,
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isDraggable, isSortable }) =>
		isDraggable &&
		!isSortable && {
			paddingRight: token('space.100', '8px'),
			cursor: 'grab',
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isIconPersistent }) =>
		isIconPersistent && {
			backgroundColor: token('color.background.neutral.subtle.hovered', '#091E420F'),
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isDropdownMenuOpen }) =>
		isDropdownMenuOpen && {
			...dragHandleStyles,
			// when the drag handle is visible, margin needs to be adjusted to accomodate
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			marginRight: getMarginBetweenHeaders(),
		},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DragHandleButton = styled.button<{ isSelected?: boolean }>({
	height: buttonHeight,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ isSelected }) =>
		isSelected ? token('color.background.neutral.subtle.pressed', '#091E4224') : 'transparent',
	borderTopLeftRadius: token('border.radius.100', '3px'),
	borderBottomLeftRadius: token('border.radius.100', '3px'),
	borderTopRightRadius: '0px',
	borderBottomRightRadius: '0px',
	padding: token('space.0', '0'),
	border: 'none',
	cursor: 'grab',
	display: 'flex',
	alignItems: 'center',
	width: `var(${dragHandleWidthCssVar})`,
	opacity: `var(${dragHandleOpacityCssVar})`,
	overflow: 'hidden',
	transition:
		'width 150ms ease-in-out, opacity 150ms ease-in-out, background-color 150ms ease-in-out',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered', '#091E420F'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':active': {
		backgroundColor: token('color.background.neutral.subtle.pressed', '#091E4224'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':focus': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		outline: `2px solid ${token('color.border.focused', colors.B100)}`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'&:not(:focus-visible)': {
			outline: 'none',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':focus-visible, &[aria-expanded="true"]': {
		opacity: 1,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		width: getDragIconSize(),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DragHandleIconContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SortButton = styled.button<{
	isDraggable: boolean;
	isDragging: boolean;
}>(
	{
		paddingTop: token('space.0', '0px'),
		paddingBottom: token('space.0', '0px'),
		height: buttonHeight,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		paddingLeft: ({ isDraggable }) =>
			isDraggable ? token('space.0', '0px') : token('space.100', '8px'),
		paddingRight: token('space.100', '8px'),
		border: 'none',
		borderTopLeftRadius: '0px',
		borderBottomLeftRadius: '0px',
		borderTopRightRadius: token('border.radius.100', '3px'),
		borderBottomRightRadius: token('border.radius.100', '3px'),
		cursor: 'pointer',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: token('color.text', colors.N500),
		backgroundColor: 'transparent',
		fontWeight: 'inherit',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		':focus': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			outline: `2px solid ${token('color.border.focused', colors.B100)}`,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
			'&:not(:focus-visible)': {
				outline: 'none',
			},
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		':hover, :focus-visible': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			[`[data-component-selector="${SORT_ICON_SELECTOR}"]`]: {
				opacity: 1,
				transitionDelay: '150ms',
			},
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isDragging }) => isDragging && { opacity: '0.4' },
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CellContent = styled.div({
	display: 'flex',
	alignItems: 'center',
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropTarget = styled.div<{ height: number; columnType: ColumnType }>({
	position: 'absolute',
	top: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: ({ height }) => `${height}px`,
	// The 1px alignment is required here because we are accounting for a border width.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	left: ({ columnType }) => (columnType === 'first' ? '1px' : 0),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	right: ({ columnType }) => (columnType === 'last' ? '1px' : 0),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DragPreview = styled.div<{ height: number; width: number }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: ({ height }) => `${height}px`,
	maxHeight: '100vh',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ width }) => `${width}px`,
	borderColor: token('color.border.focused', '#388BFF'),
	borderStyle: 'solid',
	borderRadius: token('border.radius.100', '4px'),
	borderWidth: token('border.width.outline', '2px'),
	backgroundColor: token('color.blanket.selected', '#388BFF14'),
});
